import './styles.scoped.css'
import {useEffect, useState} from 'react'
import {Typography, Button, IconButton, Avatar, Skeleton} from '@mui/material'
import {Edit, Menu} from '@mui/icons-material'
import {accountApi} from '../../services'
import {useAuth} from '../../contexts/AuthContext'
import {useGlobalStates} from '../../contexts/GlobalStatesContext'
import {useNavigate} from 'react-router-dom'
import {useMobile} from '../../hooks'
import {parseError} from '../../utils/helpers'
import {TextSkeleton, skeletonBusyProps} from '../../components'

// SkeletonContainer's busy-region attributes, put on the real card so its Edit
// button is not remounted (and a click lost) when the data lands.

// Stands in for one value until the organization arrives.
const valueOr = (skeleton, width, value) => (skeleton ? <TextSkeleton width={width} fontSize={16} /> : value)

export default () => {
    const [organization, setOrganization] = useState(null)
    const [skeleton, setSkeleton] = useState(true)
    const {permissions} = useAuth()
    const {setMessageBox, setMobileSidebar} = useGlobalStates()
    const navigate = useNavigate()
    const {mobile} = useMobile()

    useEffect(() => {
        if (mobile) {
            setMobileSidebar(false)
        }

        setSkeleton(true)
        accountApi
            .getOrganization()
            .then(({data: {data}}) => {
                setSkeleton(false)
                setOrganization(data)
            })
            .catch(error => {
                setSkeleton(false)
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }, [])

    return (
        <>
            {mobile ? (
                <>
                    <div className="header">
                        <div className="title">
                            <IconButton onClick={() => setMobileSidebar(true)}>
                                <Menu />
                            </IconButton>
                            <Typography variant="h5" color="text.primary">
                                Organization Settings
                            </Typography>
                        </div>
                        {permissions?.includes('admin.settings.write') && (
                            <IconButton onClick={() => navigate('/organization-settings/edit-organization-settings')}>
                                <Edit />
                            </IconButton>
                        )}
                    </div>
                    <br />
                </>
            ) : (
                <Typography variant="h5" color="text.primary">
                    Organization Settings
                </Typography>
            )}
            <br />
            <br />
            <div className="card" {...skeletonBusyProps(skeleton)}>
                {!mobile && (
                    <div className="header">
                        <Typography variant="h6" color="text.primary">
                            Organization Information
                        </Typography>
                        {permissions?.includes('admin.settings.write') && (
                            <Button
                                variant="outlined"
                                startIcon={<Edit />}
                                onClick={() => navigate('/organization-settings/edit-organization-settings')}>
                                Edit
                            </Button>
                        )}
                    </div>
                )}
                <br />
                <Typography variant="h7" color="primary.dark">
                    General
                </Typography>
                <br />
                <br />
                <div className="form" style={{height: 80, alignItems: 'center'}}>
                    {!mobile && (
                        <Typography variant="h8" color="text.primary" style={{width: 230}}>
                            Logo
                        </Typography>
                    )}
                    {skeleton ? (
                        <Skeleton variant="rounded" width={80} height={80} />
                    ) : organization?.logo_url ? (
                        <img src={organization.logo_url} height={80} alt="Organization logo" />
                    ) : (
                        <Avatar variant="rounded" style={{width: 80, height: 80, backgroundColor: '#0d5d5933', color: '#0d5d59'}}>
                            {organization?.name?.charAt(0)}
                        </Avatar>
                    )}
                </div>
                <div className="form">
                    <Typography className="form-label" variant="h8" color="text.primary">
                        Healthcare Name{mobile ? ':' : ''}
                    </Typography>
                    <Typography className="form-value" variant="body1" color="text.primary">
                        {valueOr(skeleton, 180, organization?.name)}
                    </Typography>
                </div>
                <div className="form">
                    <Typography className="form-label" variant="h8" color="text.primary">
                        Email{mobile ? ':' : ''}
                    </Typography>
                    <Typography className="form-value" variant="body1" color="text.primary">
                        {valueOr(skeleton, 200, organization?.email)}
                    </Typography>
                </div>
                <div className="form">
                    <Typography className="form-label" variant="h8" color="text.primary">
                        Phone Number{mobile ? ':' : ''}
                    </Typography>
                    <Typography className="form-value" variant="body1" color="text.primary">
                        {valueOr(skeleton, 120, organization?.phone)}
                    </Typography>
                </div>
                <div className="form" style={{alignItems: 'start'}}>
                    <Typography className="form-label" variant="h8" color="text.primary">
                        Business Address{mobile ? ':' : ''}
                    </Typography>
                    {skeleton ? (
                        <div style={{width: 260}}>
                            <TextSkeleton width="100%" fontSize={16} />
                            <TextSkeleton width="55%" fontSize={16} />
                        </div>
                    ) : (
                        <Typography variant="body1" color="text.primary" style={{maxWidth: 300}}>
                            {`${organization?.address?.street}${organization?.address?.street2 ? ', ' + organization?.address?.street2 : ''}, ${
                                organization?.address?.city
                            }, ${organization?.address?.state}, ${organization?.address?.zip}`}
                        </Typography>
                    )}
                </div>
                <br />
                <br />
                <Typography variant="h7" color="primary.dark">
                    Contact Person
                </Typography>
                <br />
                <br />
                <div className="form">
                    <Typography className="form-label" variant="h8" color="text.primary">
                        Name{mobile ? ':' : ''}
                    </Typography>
                    <Typography className="form-value" variant="body1" color="text.primary">
                        {valueOr(skeleton, 150, organization?.contact_name)}
                    </Typography>
                </div>
                <div className="form">
                    <Typography className="form-label" variant="h8" color="text.primary">
                        Phone Number{mobile ? ':' : ''}
                    </Typography>
                    <Typography className="form-value" variant="body1" color="text.primary">
                        {valueOr(skeleton, 120, organization?.contact_phone)}
                    </Typography>
                </div>
                <div className="form">
                    <Typography className="form-label" variant="h8" color="text.primary">
                        Email{mobile ? ':' : ''}
                    </Typography>
                    <Typography className="form-value" variant="body1" color="text.primary">
                        {valueOr(skeleton, 190, organization?.contact_email)}
                    </Typography>
                </div>
            </div>
        </>
    )
}
